// AboutPage.jsx
// About Us page — React + Tailwind + MUI Icons
// Reuses: Nav, Footer, ParticleCanvas from your existing components
// Reuses: Reveal from lib/hooks.jsx

import Nav           from "./components/Nav";
import Footer        from "./components/Footer";
import ParticleCanvas from "./components/ParticleCanvas";
import { Reveal }    from "./lib/hooks";
import React from "react";
import { useTheme } from "../../contexts/theme-context";
// import { Helmet } from "react-helmet-async";

import CheckIcon       from "@mui/icons-material/Check";
import RemoveIcon      from "@mui/icons-material/Remove";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon  from "@mui/icons-material/AutoAwesome";
import GavelIcon        from "@mui/icons-material/Gavel";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SilkRoadSection from "./components/SilkRoadSection";
import FinalCTA from "./components/FinalCTA";

// ─── DATA ────────────────────────────────────────────────

const HOW_CARDS = [
  {
    num:   "01",
    color: "text-pink",
    lineColor: "bg-pink",
    title: "Find the right track in under 60 seconds.",
    body:  "One million tracks from professional composers. MAYA — our AI — searches by mood, scene, reference link, or a detailed description in your language. MAYA understands context. Discovery is instant.",
    hoverShadow: "hover:shadow-[0_16px_40px_rgba(233,30,140,0.1)]",
  },
  {
    num:   "02",
    color: "text-navy",
    lineColor: "bg-navy",
    title: "One clear licence. Monetised forever.",
    body:  "Generate and download a licence in your name — global, perpetual, and airtight. Pay once. Use your production forever. No royalties owed down the line. No surprises when your subscription ends.",
    hoverShadow: "hover:shadow-[0_16px_40px_rgba(27,58,107,0.1)]",
  },
  {
    num:   "03",
    color: "text-teal",
    lineColor: "bg-teal",
    title: "Composers keep earning. Every play.",
    body:  "Unlike royalty-free platforms, our catalogue is fully registered with performing rights organisations worldwide. Every time your content plays, composers earn performance royalties, at zero extra cost to you.",
    hoverShadow: "hover:shadow-[0_16px_40px_rgba(10,158,120,0.1)]",
  },
];

const GOOD_POINTS = [
  "Performance royalties flow to composers via broadcasters and platforms — not from you",
  "Sustainable income attracts professional talent — the catalogue keeps getting stronger",
  "All cue sheet reporting and royalty distribution handled automatically behind the scenes",
];

const BAD_POINTS = [
  "Without license and society registration, your content is vulnerable to copyright claims",
  "Composers are stripped of ownership",
  "Your money goes to the platform, not to the composers",
  "Top talent does not work outside the royalty system - which affects catalogue quality",
];

// ─── HERO ─────────────────────────────────────────────────

function AboutHero() {
  return (
    <section className="
      min-h-[88vh] flex flex-col items-start justify-end
      px-[8%] py-20 relative overflow-hidden
      bg-[#f4f6fb] dark:bg-[#0d1117]
    "
    style={{background : "rgb(var(--canvas))"}}>
      {/* Orbs */}
      <div className="
        absolute w-[700px] h-[700px] rounded-full pointer-events-none
        -top-48 -right-48 blur-[90px] animate-orb-2
        bg-[radial-gradient(circle,rgba(233,30,140,0.12)_0%,transparent_70%)]
      " />
      <div className="
        absolute w-[500px] h-[500px] rounded-full pointer-events-none
        -bottom-24 left-[5%] blur-[90px] animate-orb-1
        bg-[radial-gradient(circle,rgba(27,58,107,0.1)_0%,transparent_70%)]
      " />
      <div className="
        absolute w-[300px] h-[300px] rounded-full pointer-events-none
        top-[30%] left-[40%] blur-[90px] animate-orb-1
        bg-[radial-gradient(circle,rgba(10,158,120,0.08)_0%,transparent_70%)]
      " />

      {/* Grid */}
      <div className="
        absolute inset-0 animate-grid-pulse
        [background-image:linear-gradient(rgba(27,58,107,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(27,58,107,0.05)_1px,transparent_1px)]
        [background-size:64px_64px]
        [mask-image:radial-gradient(ellipse_90%_90%_at_30%_60%,black_0%,transparent_100%)]
      " />

      {/* Content */}
      <div className="relative z-10 max-w-full mx-20 text-start">
        {/* Label */}
        <div className="
          section-label
        ">
          Our Philosophy
        </div>

        {/* Headline */}
        <h1 className="
          header-h1 mb-8 overflow-hidden
        ">
          {[
            "Music works",
            <span key="b">when <span className="pink-text">everyone</span> benefits</span>,
          ].map((line, i) => (
            <span
              key={i}
              className="block overflow-hidden line-reveal"
              style={{ animationDelay: `${0.15 + i * 0.15}s` }}
            >
              {line}
            </span>
          ))}
        </h1>

        {/* Sub */}
        <p className="
          lg:w-2/3 about-text
        ">
          Music and film are made for each other. Composers want their music used. Content creators want music that elevates their projects. The attraction was never the problem: traditional licensing just made the process exhausting. SyncMama is the matchmaker who gets it right. Swipe right!
        </p>
      </div>
    </section>
  );
}

// ─── MISSION ──────────────────────────────────────────────

function MissionSection() {
  return (
    <section className="relative overflow-hidden bg-[#eef2f9] dark:bg-[#161b22]"
    style={{background : "rgb(var(--canvas))"}}>
      <div className="mx-auto section-inner">
        <Reveal>
          <div className="section-label">The right system</div>
          <h2 className="section-title">
            You are a creator too.<br />We get it.
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-8 items-start mt-4">
          {/* Body text */}
          <Reveal className="mission-text space-y-6">
            <p>
              Whether you are making films, ads, podcasts, games, or anything in between — you need music that elevates your story, fits your budget, and clears all the legal hurdles. But every time you choose music, you are not just picking a sound.{" "}
              <strong >You are choosing a system.</strong>
            </p>
            <p>
              Royalty-free licensing platforms were built for a world that no longer exists. SyncMama is built into the global royalty system.
            </p>
            <p>
              When composers are paid fairly and continuously, they keep creating. That means deeper catalogues, more diverse voices, and music made by professionals who spent careers scoring visuals.{" "}
              <strong >The quality of the music you license is directly connected to the sustainability of the system that produces it.</strong>
            </p>
          </Reveal>

          {/* Pull quote */}
          <Reveal delay={120} className="md:sticky md:top-[100px]">
            <div className="
              desc-card
            ">
              <blockquote className="
                lic-step-title mb-4
              ">
                "Great composers don't work for one-time fees. They work in the system that
                respects their art. [...] When SyncMama has one million tracks by professional
                composers, what exactly is AI-generated music solving?"
              </blockquote>
              <cite className="stat-label not-italic">
                THE SYNCMAMA TEAM
              </cite>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ─── HOW IT WORKS ─────────────────────────────────────────

function HowItWorksSection() {
  return (
    <section className="relative overflow-hidden bg-[#f4f6fb] dark:bg-[#0d1117]"
    style={{background : "rgb(var(--canvas))"}}>

      <div className="mx-auto section-inner">
        <Reveal>
          <div className="section-label">Fast, Legal &amp; Fair</div>
          <h2 className="section-title">
            All three.<br />At the same time.
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-3 mt-14">
          {HOW_CARDS.map((card, i) => (
            <Reveal key={card.num} delay={i * 70}>
              <div className={`
                bg-white dark:bg-[#161b22]
                border border-[rgba(27,58,107,0.08)] dark:border-[rgba(255,255,255,0.06)]
                rounded-xl p-6 relative overflow-hidden
                transition-all duration-300
                hover:-translate-y-1 ${card.hoverShadow}
                group
                after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0
                after:h-0.5 after:bg-gradient-to-r after:from-pink after:to-navy
                after:scale-x-0 after:origin-left
                after:transition-transform after:duration-[350ms]
                hover:after:scale-x-100 h-full plan-card
              `}>
                <div className={`w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center
                    bg-pink-100 border border-pink-200
                    font-mono text-sm text-pink-500
                    transition-all duration-250
                    group-hover:bg-pink-100 group-hover:scale-105 mb-6`}>
                  {/* <span className={`w-8 h-8 border block`} /> */}
                  {card.num}
                </div>
                <h3 className="use-title mb-3">
                  {card.title}
                </h3>
                <p className="ai-text">
                  {card.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TRUTH TABLE (SyncMama vs Royalty-Free) ───────────────

function TruthSection() {
  return (
    <section className="relative overflow-hidden bg-[#f4f6fb] dark:bg-[#0d1117]"
    style={{background : "rgb(var(--canvas))"}}>
      <div className="mx-auto section-inner">
        <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Good — SyncMama */}
          <Reveal delay={50}>
            <div className="
              p-8 rounded-xl h-full
              bg-[image:linear-gradient(white,white),linear-gradient(135deg,#E91E8C,#1B3A6B)]
              dark:bg-none dark:bg-[#21262d]
              [background-origin:padding-box,border-box]
              border dark:border-pink-200 lic-card
            ">
              <div className="inline-flex items-center gap-1.5 text-sm font-bold tracking-[1.5px] uppercase px-2.5 py-1 rounded-sm mb-5 bg-pink-100 text-pink-500 border border-pink-200">
                <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                SyncMama
              </div>
              <h3 className="use-title mb-4">
                You pay. Composers are paid. You're protected.
              </h3>
              <div className="flex flex-col gap-2.5">
                {GOOD_POINTS.map(pt => (
                  <div key={pt} className="flex items-start gap-2.5 ai-text ">
                    <CheckIcon sx={{ fontSize: 14, color: "#0a9e78", flexShrink: 0, marginTop: "2px" }} />
                    {pt}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Bad — Royalty-Free */}
          <Reveal delay={120}>
            <div className="
              p-8 rounded-xl h-full opacity-80
              bg-white dark:bg-[#21262d]
              border border-[rgba(27,58,107,0.1)] dark:border-[rgba(255,255,255,0.06)]
            ">
              <div className="inline-flex items-center gap-1.5 text-sm font-bold tracking-[1.5px] uppercase px-2.5 py-1 rounded-sm mb-5 bg-gray-100 navy-text-1">
                <RemoveIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                Royalty-Free Platforms
              </div>
              <h3 className="use-title navy-text-1 mb-4">
                You pay. Composers are not paid. You still carry the risk.
              </h3>
              <div className="flex flex-col gap-2.5">
                {BAD_POINTS.map(pt => (
                  <div key={pt} className="flex items-start gap-2.5 ai-text">
                    {/* <span className="flex-shrink-0 mt-2 w-3.5 h-3.5 flex items-center justify-center">
                      <span className="w-3 h-0.5 bg-gray-300 block" />
                    </span> */}
                    <RemoveIcon className="mt-2" sx={{ fontSize: 12, opacity: 0.5 }} />
                    {pt}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </section>
  );
}

// ─── AI SECTION ───────────────────────────────────────────

function AISection() {
  return (
    <section className="relative overflow-hidden bg-[#f4f6fb] dark:bg-[#0d1117]"
    style={{background : "rgb(var(--canvas))"}}>
      <div className="section-inner mx-auto">
        <Reveal>
          <div className="section-label">AI-Generated Music</div>
          <h2 className="section-title">
            One million reasons why AI-generated music is a solution without a problem.
          </h2>
        </Reveal>

        <Reveal delay={80}>
          <div className="
            mt-14 p-10 rounded-xl
            space-y-5 desc-card
          ">
            <p className="ai-text">
              There is one more thing worth saying. The models that generate AI music were trained on compositions made by human composers - who received nothing for it. Choosing AI-generated music is not a neutral act. It is a choice to replace those composers twice: first by using their work without consent to train a machine, then by using the machine instead of them.
            </p>
            <p className="ai-text">
              SyncMama was built for the opposite. One million tracks. Professional composers. Royalties that flow automatically. All the music you will ever need. Fair. Fast. Human.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── MANIFESTO (gradient section) ─────────────────────────

function ManifestoSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-pink-500 to-blue-900">
      {/* Dot overlay */}
      <div className="
        absolute inset-0 z-0 pointer-events-none
        [background-image:radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)]
        [background-size:32px_32px]
      " />
      {/* Glow overlays */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(233,30,140,0.1)_0%,transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_10%_80%,rgba(27,58,107,0.12)_0%,transparent_50%)] pointer-events-none" />

      <div className="mx-auto section-inner relative z-10">
        <Reveal>
          <div className="flex items-center gap-2.5 font-mono text-[12px] tracking-[3px] uppercase text-white mb-6 before:content-[''] before:w-6 before:h-px before:bg-white/60">
            Mama Syncs Different
          </div>
        </Reveal>
        <Reveal delay={60}>
          <h2 className="
            header-h1-white mb-12
          ">
            A platform<br />
            <span className="text-[#f4f6fb]/90">that works for all</span>
          </h2>
        </Reveal>

        <Reveal delay={120}>
          <div className="mission-text-white max-w-[680px] mb-10 space-y-6">
            <p>
              All creators deserve to get paid. That sounds obvious — but for decades, the industry has made it anything but. Royalty-free platforms stripped composers of ownership. Then AI-generated music went further: it built itself on compositions without consent, and sells the output as a substitute for the people who made it possible.{" "}
              <strong >We built the alternative.</strong>
            </p>
            <p>
              SyncMama was built by people who spent their careers suporting creators. They built publishing companies, stood up for music creators, and made the case until lawmakers acted — enshrining something simple and radical: that artists have a right to royalty that nobody can ever take away from them. Not a label. Not a platform. Nobody.
            </p>
            <p>
              A platform where creators are successful professionals, not hamsters on a wheel, and where every licence puts money back into the hands of the artists who made the music.
            </p>
            <p>
              <strong >Supporting talent should not be complicated. Mama built it into the system. At no extra cost to you.</strong>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────

function AboutFinalCTA() {
  return (
    <section className="relative overflow-hidden bg-[#f4f6fb] dark:bg-[#0d1117] px-[6%] py-[120px] text-center"
    style={{background : "rgb(var(--canvas))"}}>
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(circle,rgba(233,30,140,0.07)_0%,transparent_70%)]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none translate-x-1/2 translate-y-1/2 bg-[radial-gradient(circle,rgba(27,58,107,0.07)_0%,transparent_70%)]" />

      <Reveal className="relative z-10">
        <h2 className="
          header-h1 mb-5
        ">
          Start on the<br />
          <span className="
            bg-gradient-to-r from-pink-500 via-blue-900 to-pink-500
            bg-[length:200%_auto]
            bg-clip-text text-transparent
            [-webkit-background-clip:text]
            animate-[shimmer_4s_linear_infinite]
          ">
            right side
          </span>{" "}of music.
        </h2>
        <p className="mx-auto mb-10 pricing-sub-header">
          Join the platform built for creators and composers alike.
        </p>
        {/* <div className="
          flex items-center gap-4 justify-center flex-wrap mb-1
          [animation:fadeUp_0.8s_0.75s_cubic-bezier(.4,0,.2,1)_both]
        ">
          <span className="flex-wrap jusitify-center">
            <a href="/register/free" 
              className="pink-btn">
              Start Free
              <ArrowForwardIcon sx={{ fontSize: 20 }} className="transition-transform duration-200 group-hover:translate-x-1 mx-1" />
            </a>
            <p className="my-4 feature-desc relative">
              Unlock AI search and all features. No card.
            </p>
          </span>
          <span className="pr-4 flex justify-center lic-step-title" style={{marginTop: "-48px"}}>or</span>
          <span className="flex-wrap justify-center">
            <a href="/" 
              className="ghost-btn">
              Explore The Catalogue
            </a>
            <p className="my-4 feature-desc relative">
              No sign up. Browse and keyword search only.
            </p>
          </span>
        </div> */}
      </Reveal>
    </section>
  );
}

// ─── ROOT ─────────────────────────────────────────────────

export default function AboutPage() {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === "dark";

  return (
    <div className={dark ? "dark" : ""}>
      {/* <Helmet>
        <title>About SyncMama | Music Licensing Built for Creators</title>
        <meta name="description" content="SyncMama was built by music industry veterans who believe great composers deserve to earn fairly — and creators deserve music that is fast, legal, and transparent." />
        <meta property="og:title" content="About SyncMama" />
        <meta property="og:url" content="https://syncmama.com/about" />
        <link rel="canonical" href="https://syncmama.com/about" />
      </Helmet> */}
      <div className="
        relative min-h-screen overflow-x-hidden
        bg-[#f4f6fb] dark:bg-[#0d1117]
        text-[#1a2540] dark:text-[#e6edf3]
        font-dm
      ">
        {/* <ParticleCanvas /> */}
        <Nav dark={dark} onToggleDark={toggleTheme} />
        <main>
          <AboutHero />
          <MissionSection />
          <HowItWorksSection />
          {/* <TruthSection /> */}
          {/* <AISection /> */}
          <SilkRoadSection />
          <ManifestoSection />
          <AboutFinalCTA />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </div>
  );
}
